import React, { useMemo, useState, useEffect } from 'react';
import { Siswa, TabunganSiswa } from '../../types/index.js';
import { api } from '../../services/api.js';
import { Wallet, TrendingUp, TrendingDown, Store, Coffee, ShieldAlert, CreditCard, Activity, ArrowUpRight, ArrowDownRight, Zap } from 'lucide-react';

interface DashboardEWalletViewProps {
  siswaList: Siswa[];
}

export const DashboardEWalletView: React.FC<DashboardEWalletViewProps> = ({ siswaList }) => {
  // Simulate E-Wallet transactions based on tabungan data for now
  const totalLiquidity = useMemo(() => siswaList.reduce((sum, s) => sum + (s.saldoEWallet || 0), 0), [siswaList]);
  const activeWallets = useMemo(() => siswaList.filter(s => (s.saldoEWallet || 0) > 0).length, [siswaList]);
  
  const [mutasiList, setMutasiList] = useState<TabunganSiswa[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMutasi();
  }, []);

  const loadMutasi = async () => {
    try {
      const data = await api.getMutasiEWallet();
      setMutasiList(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const todayStr = new Date().toLocaleDateString('id-ID');
  
  const mutasiHariIni = useMemo(() => {
    return mutasiList.filter(m => new Date(m.tanggal).toLocaleDateString('id-ID') === todayStr);
  }, [mutasiList, todayStr]);

  const dailySpendKantin = useMemo(() => {
    return mutasiHariIni.filter(m => m.jenis === 'TARIK' && m.keterangan.toLowerCase().includes('kantin')).reduce((sum, m) => sum + m.nominal, 0);
  }, [mutasiHariIni]);

  const dailySpendKoperasi = useMemo(() => {
    return mutasiHariIni.filter(m => m.jenis === 'TARIK' && m.keterangan.toLowerCase().includes('koperasi')).reduce((sum, m) => sum + m.nominal, 0);
  }, [mutasiHariIni]);
  
  const dailySpendLainnya = useMemo(() => {
    return mutasiHariIni.filter(m => m.jenis === 'TARIK' && !m.keterangan.toLowerCase().includes('kantin') && !m.keterangan.toLowerCase().includes('koperasi')).reduce((sum, m) => sum + m.nominal, 0);
  }, [mutasiHariIni]);

  const totalTopupHariIni = useMemo(() => {
    return mutasiHariIni.filter(m => m.jenis === 'SETOR').reduce((sum, m) => sum + m.nominal, 0);
  }, [mutasiHariIni]);

  const latestTransactions = useMemo(() => {
    return [...mutasiList].sort((a, b) => new Date(b.tanggal).getTime() - new Date(a.tanggal).getTime()).slice(0, 5);
  }, [mutasiList]);

  const topSpenders = useMemo(() => {
    const spenders: Record<string, number> = {};
    mutasiHariIni.filter(m => m.jenis === 'TARIK').forEach(m => {
      spenders[m.siswaId] = (spenders[m.siswaId] || 0) + m.nominal;
    });
    return Object.entries(spenders)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([siswaId, amount]) => {
        const siswa = siswaList.find(s => s.id === siswaId);
        return { id: siswaId, nama: siswa?.nama || 'Unknown', amount };
      });
  }, [mutasiHariIni, siswaList]);

  const anomalies = useMemo(() => {
    const list: Array<{ id: string; nama: string; reason: string; level: 'danger' | 'warning' }> = [];
    const txCount: Record<string, number> = {};
    const totalAmount: Record<string, number> = {};
    
    // Group transactions by student for today
    mutasiHariIni.filter(m => m.jenis === 'TARIK').forEach(m => {
      txCount[m.siswaId] = (txCount[m.siswaId] || 0) + 1;
      totalAmount[m.siswaId] = (totalAmount[m.siswaId] || 0) + m.nominal;
      
      // Anomaly: single large transaction
      if (m.nominal >= 200000) {
        const siswa = siswaList.find(s => s.id === m.siswaId);
        if (siswa && !list.find(l => l.id === m.siswaId && l.reason.includes('Penarikan besar'))) {
          list.push({
            id: m.siswaId,
            nama: `${siswa.nama} (${siswa.kelasNama})`,
            reason: `Penarikan tunai Rp ${m.nominal.toLocaleString('id-ID')} terpantau besar`,
            level: 'warning'
          });
        }
      }
    });

    // Anomaly: high frequency (velocity)
    Object.entries(txCount).forEach(([siswaId, count]) => {
      if (count >= 5) {
        const siswa = siswaList.find(s => s.id === siswaId);
        if (siswa && !list.find(l => l.id === siswaId && l.reason.includes('frekuensi'))) {
          list.push({
            id: siswaId,
            nama: `${siswa.nama} (${siswa.kelasNama})`,
            reason: `Frekuensi belanja tinggi: ${count}x transaksi hari ini (Total Rp ${totalAmount[siswaId].toLocaleString('id-ID')})`,
            level: 'danger'
          });
        }
      }
    });

    return list;
  }, [mutasiHariIni, siswaList]);

  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'SECURITY'>('OVERVIEW');

  // E-Wallet Settings State
  const [settings, setSettings] = useState({
    limitKantin: Number(localStorage.getItem('ewallet_limitKantinAmount')) || 50000,
    pinRequired: localStorage.getItem('ewallet_pinRequired') !== 'false' // default true
  });

  const updateSetting = (key: keyof typeof settings, value: any) => {
    localStorage.setItem(`ewallet_${key === 'limitKantin' ? 'limitKantinAmount' : 'pinRequired'}`, String(value));
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) return <div className="p-6 text-center text-slate-500 animate-pulse">Memuat analitik realtime...</div>;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* Header section with gradient */}
      <div className="bg-gradient-to-br from-indigo-900 via-indigo-800 to-violet-900 rounded-2xl p-6 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <CreditCard className="w-64 h-64 text-white transform rotate-12" />
        </div>
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="text-white space-y-2">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-bold tracking-wider uppercase backdrop-blur-md">
                E-Wallet Command Center
              </span>
              <span className="flex h-3 w-3 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
              </span>
            </div>
            <h1 className="text-3xl font-black tracking-tight">Rp {totalLiquidity.toLocaleString('id-ID')}</h1>
            <p className="text-indigo-200 text-sm font-medium">Total Likuiditas Beredar (Circulating Supply)</p>
          </div>

          <div className="flex gap-4">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
              <p className="text-indigo-200 text-xs font-bold uppercase mb-1">Active Wallets</p>
              <p className="text-2xl font-black text-white">{activeWallets} <span className="text-sm font-normal text-indigo-300">Siswa</span></p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
              <p className="text-indigo-200 text-xs font-bold uppercase mb-1">Velocity (24h)</p>
              <p className="text-2xl font-black text-emerald-400">+12.4%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('OVERVIEW')}
          className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'OVERVIEW' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Overview & Analisis Belanja
        </button>
        <button
          onClick={() => setActiveTab('SECURITY')}
          className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'SECURITY' ? 'border-rose-600 text-rose-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          <ShieldAlert className="w-4 h-4" />
          Fraud & Security
        </button>
      </div>

      {activeTab === 'OVERVIEW' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Spend Analytics */}
          <div className="md:col-span-2 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform">
                  <Coffee className="w-24 h-24 text-orange-500" />
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600">
                    <Coffee className="w-4 h-4" />
                  </div>
                  <h3 className="font-bold text-slate-700">Volume Kantin (Hari Ini)</h3>
                </div>
                <p className="text-2xl font-black text-slate-900 mt-4">Rp {dailySpendKantin.toLocaleString('id-ID')}</p>
                <div className="flex items-center gap-1 mt-2 text-emerald-600 text-xs font-bold">
                  <TrendingUp className="w-3 h-3" />
                  <span>+5.2% vs kemarin</span>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform">
                  <Store className="w-24 h-24 text-sky-500" />
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center text-sky-600">
                    <Store className="w-4 h-4" />
                  </div>
                  <h3 className="font-bold text-slate-700">Volume Koperasi (Hari Ini)</h3>
                </div>
                <p className="text-2xl font-black text-slate-900 mt-4">Rp {dailySpendKoperasi.toLocaleString('id-ID')}</p>
                <div className="flex items-center gap-1 mt-2 text-rose-600 text-xs font-bold">
                  <TrendingDown className="w-3 h-3" />
                  <span>-2.1% vs kemarin</span>
                </div>
              </div>
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform">
                  <Wallet className="w-24 h-24 text-indigo-500" />
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600">
                    <Wallet className="w-4 h-4" />
                  </div>
                  <h3 className="font-bold text-slate-700">Volume Lainnya (Hari Ini)</h3>
                </div>
                <p className="text-2xl font-black text-slate-900 mt-4">Rp {dailySpendLainnya.toLocaleString('id-ID')}</p>
                <div className="flex items-center gap-1 mt-2 text-indigo-600 text-xs font-bold">
                  <Activity className="w-3 h-3" />
                  <span>Real-time</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
                <Activity className="w-5 h-5 text-indigo-500" />
                Real-time Transaction Feed
              </h3>
              <div className="space-y-4">
                {latestTransactions.map(trx => {
                  const isKantin = trx.keterangan.toLowerCase().includes('kantin');
                  const isKoperasi = trx.keterangan.toLowerCase().includes('koperasi');
                  const isSetor = trx.jenis === 'SETOR';
                  
                  return (
                  <div key={trx.id} className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg border border-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isKantin ? 'bg-orange-100 text-orange-600' : isKoperasi ? 'bg-sky-100 text-sky-600' : isSetor ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-600'}`}>
                        {isKantin ? <Coffee className="w-5 h-5" /> : isKoperasi ? <Store className="w-5 h-5" /> : isSetor ? <ArrowDownRight className="w-5 h-5" /> : <ArrowUpRight className="w-5 h-5" />}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-800">{trx.namaSiswa || 'Siswa'}</p>
                        <p className="text-xs text-slate-500">{trx.keterangan || (isSetor ? 'Top Up Saldo' : 'Penarikan/Belanja')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-black ${isSetor ? 'text-emerald-600' : 'text-slate-700'}`}>
                        {isSetor ? '+' : '-'} Rp {trx.nominal.toLocaleString('id-ID')}
                      </p>
                      <p className="text-[10px] text-slate-400 font-medium">{new Date(trx.tanggal).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar Analytics */}
          <div className="space-y-6">
            <div className="bg-slate-900 rounded-xl shadow-lg p-6 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500 rounded-full filter blur-[60px] opacity-50 translate-x-10 -translate-y-10"></div>
              <h3 className="font-bold text-slate-200 mb-2 relative z-10">Gross Inflow (Topup)</h3>
              <p className="text-3xl font-black text-white relative z-10 tracking-tight">Rp {(totalTopupHariIni / 1000000).toFixed(1)}M</p>
              <div className="mt-4 pt-4 border-t border-slate-700 relative z-10">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-400">Gateway / QRIS</span>
                  <span className="text-xs font-bold text-white">65%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-1.5 mb-3">
                  <div className="bg-sky-400 h-1.5 rounded-full" style={{ width: '65%' }}></div>
                </div>
                
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-400">Tunai Kasir</span>
                  <span className="text-xs font-bold text-white">35%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-1.5">
                  <div className="bg-emerald-400 h-1.5 rounded-full" style={{ width: '35%' }}></div>
                </div>
              </div>
            </div>

            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="font-bold text-slate-800 mb-4 text-sm">Top Spenders (Hari Ini)</h3>
              <div className="space-y-3">
                {topSpenders.length > 0 ? topSpenders.map((s, idx) => (
                  <div key={s.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-400 w-4">{idx + 1}.</span>
                      <span className="text-xs font-medium text-slate-700 truncate max-w-[120px]">{s.nama}</span>
                    </div>
                    <span className="text-xs font-bold text-rose-600">
                      - Rp {s.amount.toLocaleString('id-ID')}
                    </span>
                  </div>
                )) : (
                  <p className="text-xs text-slate-500">Belum ada pengeluaran hari ini.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'SECURITY' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 relative overflow-hidden">
            <ShieldAlert className="absolute -bottom-4 -right-4 w-32 h-32 text-rose-100" />
            <h3 className="text-rose-900 font-bold text-lg mb-2 relative z-10 flex items-center gap-2">
              <Zap className="w-5 h-5 text-rose-600" />
              Velocity Anomaly Detection
            </h3>
            <p className="text-rose-700 text-sm mb-6 relative z-10 leading-relaxed">
              {anomalies.length > 0 
                ? "Sistem mendeteksi pola belanja yang tidak wajar pada beberapa E-Wallet siswa hari ini. Segera lakukan verifikasi."
                : "Tidak ada anomali aktivitas mencurigakan yang terdeteksi hari ini. Sistem aman."}
            </p>
            
            <div className="space-y-3 relative z-10">
              {anomalies.map((anom, idx) => (
                <div key={idx} className="bg-white p-4 rounded-lg shadow-sm border border-rose-100 flex justify-between items-center">
                  <div>
                    <p className="font-bold text-slate-800 text-sm">{anom.nama}</p>
                    <p className={`text-xs mt-1 ${anom.level === 'danger' ? 'text-rose-600' : 'text-amber-600'}`}>{anom.reason}</p>
                  </div>
                  <button className="px-3 py-1.5 bg-rose-600 text-white text-xs font-bold rounded shadow-sm hover:bg-rose-700">Bekukan</button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="text-slate-800 font-bold text-lg mb-4">Pengaturan Batas Transaksi Harian E-Wallet</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <div>
                  <h4 className="font-bold text-sm text-slate-800">Limit Belanja Kantin</h4>
                  <p className="text-xs text-slate-500 mt-0.5">Maksimal pengeluaran per hari</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-700">Rp</span>
                  <input 
                    type="number"
                    value={settings.limitKantin}
                    onChange={(e) => updateSetting('limitKantin', Number(e.target.value))}
                    className="text-sm font-bold border border-slate-300 rounded px-2 py-1 w-28 text-right focus:outline-none focus:ring-1 focus:ring-sky-500"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <div>
                  <h4 className="font-bold text-sm text-slate-800">Autentikasi PIN Kantin</h4>
                  <p className="text-xs text-slate-500 mt-0.5">Wajib masukkan PIN untuk transaksi E-Wallet</p>
                </div>
                <button 
                  onClick={() => updateSetting('pinRequired', !settings.pinRequired)}
                  className={`px-3 py-1 text-xs font-bold rounded border ${settings.pinRequired ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-200 text-slate-500 border-slate-300'}`}
                >
                  {settings.pinRequired ? 'Aktif' : 'Non Aktif'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
